import { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Check, Loader2, Award, Lock, Calendar } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { trackEvent } from "../lib/analytics";
import Seo from "../components/Seo";
import {
  PILLARS,
  INDUSTRIES,
  QUESTION_BANKS,
  computeTotalScore,
  tierForScore,
  pillarLabel,
  mogulQuote,
  pillarAction,
} from "../data/assessment";

const STAGES = { intro: 0, lead: 1, quiz: 2, result: 3 };

export default function GrowthAssessment() {
  const [stage, setStage] = useState(STAGES.intro);
  const [lead, setLead] = useState({
    full_name: "", business_name: "", email: "", phone: "", industry: "hospitality", consent: false, website_field: "",
  });
  const [answers, setAnswers] = useState({});
  const [pillarIdx, setPillarIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const bank = QUESTION_BANKS[lead.industry] || QUESTION_BANKS.other;
  const pillar = PILLARS[pillarIdx];
  const pillarQs = bank[pillar.key];
  const totalPillars = PILLARS.length;

  const answeredCount = useMemo(
    () => PILLARS.reduce((n, p) => n + bank[p.key].filter((_, i) => answers[`${p.key}-${i}`] !== undefined).length, 0),
    [answers, bank]
  );
  const totalQs = PILLARS.reduce((n, p) => n + bank[p.key].length, 0);
  const progressPct = stage === STAGES.quiz ? Math.round((answeredCount / totalQs) * 100) : 0;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [stage, pillarIdx]);

  const startLead = () => {
    trackEvent("assessment_started", { industry: lead.industry });
    setStage(STAGES.lead);
  };
  const startQuiz = () => {
    if (!lead.full_name || !lead.business_name || !lead.email || !lead.phone) return toast.error("Please complete all required fields.");
    if (!lead.consent) return toast.error("Please agree to be contacted.");
    setStage(STAGES.quiz);
  };
  const chooseAnswer = (qIdx, value) => setAnswers((a) => ({ ...a, [`${pillar.key}-${qIdx}`]: value }));
  const allAnsweredInPillar = pillarQs.every((_, i) => answers[`${pillar.key}-${i}`] !== undefined);

  const nextPillar = () => {
    if (!allAnsweredInPillar) return toast.error("Please answer every question in this section.");
    if (pillarIdx < totalPillars - 1) setPillarIdx(pillarIdx + 1);
    else finish();
  };
  const prevPillar = () => { if (pillarIdx > 0) setPillarIdx(pillarIdx - 1); };

  const finish = async () => {
    setSubmitting(true);
    const { total, pillars } = computeTotalScore(answers, lead.industry);
    const payload = {
      ...lead,
      answers,
      industry: lead.industry,
      total_score: total,
      pillar_scores: {
        strategy: pillars[0], operations: pillars[1], brand: pillars[2], cx: pillars[3], leadership: pillars[4],
      },
    };
    try {
      const { data } = await api.post("/submissions/mogul-score", payload);
      trackEvent("assessment_completed", {
        score: total,
        tier: data?.tier || "",
        industry: lead.industry,
      });
    } catch (e) {
      toast.error("Your score was calculated but we couldn't save it. Please retry from the button below.");
    } finally {
      setResult({ total, pillars });
      setStage(STAGES.result);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-growth-assessment">
      <Seo
        title="Free Mogul Growth Score™ Assessment — Dallas Business Consulting"
        description="A free 25-question diagnostic that reveals exactly where your business is leaving money on the table — across Strategy, Operations, Brand, Customer Experience, and Leadership."
        path="/growth-assessment"
      />

      {stage === STAGES.intro && <Intro onStart={startLead} />}
      {stage === STAGES.lead && <LeadForm lead={lead} setLead={setLead} onBack={() => setStage(STAGES.intro)} onStart={startQuiz} />}
      {stage === STAGES.quiz && (
        <QuizStage
          pillar={pillar}
          pillarQs={pillarQs}
          pillarIdx={pillarIdx}
          totalPillars={totalPillars}
          progressPct={progressPct}
          answers={answers}
          chooseAnswer={chooseAnswer}
          onNext={nextPillar}
          onPrev={prevPillar}
          submitting={submitting}
        />
      )}
      {stage === STAGES.result && result && (
        <ResultStage result={result} lead={lead} />
      )}
    </div>
  );
}

/* ------------------------------ INTRO ------------------------------ */
function Intro({ onStart }) {
  return (
    <>
      <section className="bg-[#0A0A0A] text-white grain">
        <div className="container-wide relative z-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-6" style={{ color: "#C19B54" }}>The Mogul Growth Framework™</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight" data-testid="ga-intro-headline">
              Discover Your <span className="italic text-gold">Mogul Score™</span>.
            </h1>
            <p className="mt-6 text-lg text-[#D1D5DB] max-w-2xl leading-relaxed">
              A free 25-question diagnostic that reveals exactly where your business is leaving money on the table — and what to do about it.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-3 text-sm text-[#E5E7EB] max-w-xl">
              {["25 questions across 5 pillars", "Personalized score out of 100", "Pillar-by-pillar strengths & gaps", "Under 10 minutes to complete"].map((f) => (
                <li key={f} className="flex gap-2 items-start"><Check className="w-4 h-4 mt-0.5 text-gold shrink-0" strokeWidth={2} />{f}</li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button onClick={onStart} data-testid="ga-intro-cta" className="btn btn-primary">Get My Mogul Score™ <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></button>
              <Link to="/contact" className="btn btn-dark">Talk to Alex Directly</Link>
            </div>
            <p className="mt-6 text-xs text-[#A1A1AA] flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} /> Free · No obligation · Your info is only used to evaluate your business.</p>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/10 p-8">
              <div className="overline" style={{ color: "#C19B54" }}>The 5 Pillars</div>
              <ul className="mt-4 space-y-4">
                {PILLARS.map((p) => (
                  <li key={p.key} className="flex gap-3 items-start text-sm" data-testid={`intro-pillar-${p.key}`}>
                    <span className="text-gold text-lg leading-none">{p.icon}</span>
                    <div>
                      <div className="font-medium text-white">{p.name}</div>
                      <div className="text-xs text-[#A1A1AA] mt-0.5">5 diagnostic questions</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-wide py-16 md:py-24 grid md:grid-cols-3 gap-8">
          {[{n:"01",t:"Answer 25 questions",b:"Honest answers matter more than perfect ones. This is a diagnostic, not a test."},
            {n:"02",t:"Get your Mogul Score™",b:"See your overall score plus a breakdown across the 5 pillars of business performance."},
            {n:"03",t:"Receive personalized guidance",b:"Alex will personally review your results and follow up with practical next steps."}].map((s) => (
            <div key={s.n} className="border-t-2 border-gold pt-6" data-testid={`intro-step-${s.n}`}>
              <div className="font-serif text-3xl text-gold leading-none">{s.n}</div>
              <h3 className="mt-4 font-serif text-xl">{s.t}</h3>
              <p className="mt-2 text-sm text-[#4B5563] leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

/* ------------------------------ LEAD FORM ------------------------------ */
function LeadForm({ lead, setLead, onBack, onStart }) {
  const set = (k, v) => setLead({ ...lead, [k]: v });
  return (
    <section className="bg-[#F7F7F8]">
      <div className="container-wide py-16 md:py-20 max-w-3xl">
        <div className="overline mb-3">Step 1 of 2</div>
        <h1 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight" data-testid="ga-lead-headline">Tell us about you.</h1>
        <p className="mt-4 text-[#4B5563] max-w-2xl">So Alex can personalize your score and follow up with relevant next steps.</p>

        <div className="mt-10 bg-white p-8 md:p-10 border border-gray-100 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Full name" required><input required data-testid="ga-input-name" value={lead.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} /></Field>
            <Field label="Business name" required><input required data-testid="ga-input-business" value={lead.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} /></Field>
            <Field label="Email" required><input required type="email" data-testid="ga-input-email" value={lead.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
            <Field label="Phone" required><input required data-testid="ga-input-phone" value={lead.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></Field>
          </div>
          <Field label="Industry" required>
            <select data-testid="ga-input-industry" value={lead.industry} onChange={(e) => set("industry", e.target.value)} className={inputCls}>
              {INDUSTRIES.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
          </Field>
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: "absolute", left: "-9999px" }} value={lead.website_field} onChange={(e) => set("website_field", e.target.value)} />
          <label className="flex gap-3 items-start text-sm pt-4 border-t border-gray-100">
            <input type="checkbox" required data-testid="ga-input-consent" checked={lead.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
            <span className="text-[#4B5563]">I agree to receive my Mogul Score™ results and a follow-up from The Mogul Group Advisors. My information will only be used for that purpose.</span>
          </label>
          <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-between">
            <button onClick={onBack} className="btn btn-secondary" data-testid="ga-lead-back"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back</button>
            <button onClick={onStart} className="btn btn-primary" data-testid="ga-lead-start">Start the Assessment <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ QUIZ STAGE ------------------------------ */
function QuizStage({ pillar, pillarQs, pillarIdx, totalPillars, progressPct, answers, chooseAnswer, onNext, onPrev, submitting }) {
  return (
    <section className="bg-[#F7F7F8]">
      <div className="container-wide py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[#6B7280] mb-3">
            <span data-testid="quiz-pillar-label">Section {pillarIdx + 1} of {totalPillars} · <span className="text-gold">{pillar.name}</span></span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1 bg-gray-200 overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${progressPct}%` }} data-testid="quiz-progress" />
          </div>

          <div className="mt-8 flex items-center gap-3">
            <span className="text-3xl text-gold">{pillar.icon}</span>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight" data-testid="quiz-pillar-name">{pillar.name}</h2>
          </div>

          <div className="mt-10 space-y-6">
            {pillarQs.map((q, i) => (
              <QuestionCard key={i} idx={i} q={q} value={answers[`${pillar.key}-${i}`]} onChoose={(v) => chooseAnswer(i, v)} pillarKey={pillar.key} />
            ))}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <button onClick={onPrev} disabled={pillarIdx === 0} data-testid="quiz-prev" className="btn btn-secondary disabled:opacity-40">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Previous Section
            </button>
            <button onClick={onNext} disabled={submitting} data-testid="quiz-next" className="btn btn-primary">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {pillarIdx === totalPillars - 1 ? "Reveal My Mogul Score™" : "Next Section"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function QuestionCard({ idx, q, value, onChoose, pillarKey }) {
  return (
    <div className="bg-white p-6 md:p-8 border border-gray-100" data-testid={`question-${pillarKey}-${idx}`}>
      <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">Question {idx + 1}</div>
      <h3 className="font-serif text-lg md:text-xl leading-snug text-[#0A0A0A]">{q.q}</h3>
      {q.ctx && <p className="mt-2 text-sm text-[#6B7280] italic">{q.ctx}</p>}
      <div className="mt-5 grid gap-3">
        {q.o.map((opt, i) => {
          const selected = value === opt.v;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onChoose(opt.v)}
              data-testid={`option-${pillarKey}-${idx}-${i}`}
              className={`text-left text-sm px-5 py-4 border transition-all flex items-start gap-3 ${selected ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-gray-200 hover:border-gold hover:bg-[#FDFBF7]"}`}
            >
              <span className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${selected ? "border-gold bg-gold" : "border-gray-300"}`}>
                {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </span>
              <span className="flex-1">{opt.l}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------ RESULT STAGE ------------------------------ */
function ResultStage({ result, lead }) {
  const tier = tierForScore(result.total);
  const article = /^[aeiouAEIOU]/.test(tier.name) ? "an" : "a";
  const firstName = lead.full_name.split(" ")[0];
  const quote = mogulQuote(result.total, firstName, lead.business_name);
  const ringLength = 2 * Math.PI * 88; // r=88
  const dash = (result.total / 100) * ringLength;

  return (
    <>
      <section className="bg-[#0A0A0A] text-white grain" data-testid="result-hero">
        <div className="container-wide relative z-10 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative">
              <svg width="220" height="220" viewBox="0 0 220 220" className="-rotate-90">
                <circle cx="110" cy="110" r="88" stroke="rgba(255,255,255,0.1)" strokeWidth="14" fill="none" />
                <circle cx="110" cy="110" r="88" stroke={tier.accent} strokeWidth="14" fill="none"
                  strokeDasharray={`${dash} ${ringLength}`} strokeLinecap="round"
                  style={{ transition: "stroke-dasharray 1.2s ease-out" }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-xs uppercase tracking-widest" style={{ color: "#C19B54" }}>Your Score</div>
                <div className="font-serif text-6xl leading-none mt-2" data-testid="result-total">{result.total}</div>
                <div className="text-xs text-[#A1A1AA] mt-1">of 100</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-3" style={{ color: "#C19B54" }}>Mogul Score™ Result</div>
            <h1 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight" data-testid="result-tier">
              You're {article} <span style={{ color: tier.accent }}>{tier.name}</span>.
            </h1>
            <p className="mt-6 text-[#D1D5DB] leading-relaxed max-w-xl">{tier.body}</p>
            <blockquote className="mt-8 border-l-2 border-gold pl-5 text-[#E5E7EB] italic font-serif text-lg leading-snug" data-testid="result-quote">
              "{quote}"
              <span className="block mt-3 not-italic text-xs uppercase tracking-widest text-[#A1A1AA]">— Alex, Founder &amp; CEO</span>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-wide py-16 md:py-24">
          <div className="max-w-3xl mb-12">
            <div className="overline mb-3">Pillar Breakdown</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">How you scored across the 5 pillars.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed">Your top strengths and biggest opportunities — with a starting action for each.</p>
          </div>

          <div className="space-y-4" data-testid="result-pillars">
            {PILLARS.map((p, i) => {
              const score = result.pillars[i];
              const label = pillarLabel(score);
              const action = pillarAction(p.key, score);
              return (
                <div key={p.key} className="bg-[#F7F7F8] p-6 md:p-8" data-testid={`result-pillar-${p.key}`}>
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="text-2xl text-gold shrink-0">{p.icon}</div>
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-serif text-xl">{p.name}</h3>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-1 border" style={{ color: label.accent, borderColor: label.accent }}>{label.label}</span>
                      </div>
                      <div className="mt-3 h-2 bg-white overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${score}%`, background: label.accent }} data-testid={`result-bar-${p.key}`} />
                      </div>
                      <div className="mt-4">
                        <div className="font-serif text-base leading-tight">{action.title}</div>
                        <p className="mt-1 text-sm text-[#4B5563] leading-relaxed">{action.body}</p>
                      </div>
                    </div>
                    <div className="font-serif text-3xl text-[#0A0A0A]" data-testid={`result-score-${p.key}`}>{score}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlurredStrategyPreview lead={lead} tier={tier} />

      <CalendlyEmbed lead={lead} />

      <section className="bg-[#F7F7F8]">
        <div className="container-wide py-16 md:py-20 text-center max-w-3xl mx-auto">
          <Award className="w-8 h-8 text-gold mx-auto" strokeWidth={1.5} />
          <div className="overline mt-4 mb-3">Prefer another path?</div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Can't book right now?</h2>
          <p className="mt-4 text-[#4B5563] leading-relaxed">
            Send us a message and Alex will personally reach out to schedule your strategy session for {lead.business_name}.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-secondary" data-testid="result-cta-book">Send a Message <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            <Link to="/services" className="btn btn-ghost" data-testid="result-cta-services">Explore Our Services <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
          <p className="mt-6 text-xs text-[#6B7280]">A copy of your results has been sent to {lead.email}.</p>
        </div>
      </section>
    </>
  );
}

/* ------------------------------ BLURRED STRATEGY PREVIEW ------------------------------ */
function BlurredStrategyPreview({ lead, tier }) {
  const firstName = (lead.full_name || "").split(" ")[0] || "there";
  return (
    <section className="bg-white" data-testid="blurred-preview-section">
      <div className="container-wide pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="overline mb-3">Your Personalized Roadmap</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">
              Your Full Mogul Growth Roadmap™ is Ready.
            </h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
              Based on your score, we've prepared a personalized 90-day growth roadmap for {lead.business_name || "your business"}. Book your complimentary 30-minute strategy session below to unlock it — Alex will walk through it live with you.
            </p>
          </div>

          {/* Locked document mockup */}
          <div className="relative border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.08)] bg-white overflow-hidden" data-testid="blurred-preview-doc">
            {/* Document header */}
            <div className="bg-[#0A0A0A] text-white px-8 py-6 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#C19B54" }}>Prepared for {firstName}</div>
                <div className="font-serif text-xl mt-1">Mogul Growth Roadmap™</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">Tier</div>
                <div className="text-sm mt-1" style={{ color: tier.accent }}>{tier.name}</div>
              </div>
            </div>

            {/* Blurred content */}
            <div className="relative">
              <div className="p-8 md:p-10 space-y-6 select-none pointer-events-none" style={{ filter: "blur(6px)" }} aria-hidden="true">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold mb-2">01 · Immediate Wins (Weeks 1–2)</div>
                  <div className="h-3 bg-gray-200 w-11/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 w-10/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-9/12"></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold mb-2">02 · Structural Fixes (Weeks 3–6)</div>
                  <div className="h-3 bg-gray-200 w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 w-11/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-9/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-10/12"></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold mb-2">03 · Growth Initiatives (Weeks 7–12)</div>
                  <div className="h-3 bg-gray-200 w-10/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-11/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 w-8/12"></div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gold mb-2">04 · Recommended Engagement</div>
                  <div className="h-3 bg-gray-200 w-9/12 mb-2"></div>
                  <div className="h-3 bg-gray-200 w-11/12"></div>
                </div>
              </div>

              {/* Unlock overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/40 via-white/70 to-white">
                <div className="text-center px-6 py-10 max-w-md">
                  <div className="inline-flex items-center gap-2 px-4 py-2 border border-gold text-gold text-[11px] uppercase tracking-widest">
                    <Lock className="w-3.5 h-3.5" strokeWidth={2} /> Locked · Unlock in your strategy session
                  </div>
                  <h3 className="mt-6 font-serif text-2xl md:text-3xl leading-snug">
                    Your custom 90-day roadmap is ready for review.
                  </h3>
                  <p className="mt-4 text-sm text-[#4B5563]">
                    Book your complimentary strategy session below — Alex will walk through your personalized plan in 30 minutes.
                  </p>
                  <a href="#book-session" className="btn btn-primary mt-6" data-testid="unlock-cta">
                    Book to Unlock <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ CALENDLY INLINE EMBED ------------------------------ */
function CalendlyEmbed({ lead }) {
  const sectionRef = useRef(null);
  const viewedRef = useRef(false);
  const bookedRef = useRef(false);

  useEffect(() => {
    if (document.getElementById("calendly-widget-js")) return;
    const s = document.createElement("script");
    s.id = "calendly-widget-js";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
    const l = document.createElement("link");
    l.id = "calendly-widget-css";
    l.rel = "stylesheet";
    l.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(l);
  }, []);

  // Fire GA event when the widget scrolls into view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            trackEvent("calendly_widget_viewed", { industry: lead.industry });
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [lead.industry]);

  // Listen for Calendly's postMessage event when a session is booked
  useEffect(() => {
    const onMsg = (e) => {
      if (!e?.data || typeof e.data !== "object") return;
      if (e.data.event === "calendly.event_scheduled" && !bookedRef.current) {
        bookedRef.current = true;
        trackEvent("strategy_session_booked", {
          industry: lead.industry,
          business: lead.business_name || "",
        });
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [lead.industry, lead.business_name]);

  const prefill = new URLSearchParams({
    name: lead.full_name || "",
    email: lead.email || "",
    a1: lead.business_name || "",
    a2: lead.phone || "",
  }).toString();
  const url = `https://calendly.com/themoguladvisors-info/30min?hide_gdpr_banner=1&primary_color=c19b54&${prefill}`;

  return (
    <section id="book-session" ref={sectionRef} className="bg-white scroll-mt-24" data-testid="calendly-section">
      <div className="container-wide pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="overline mb-3 flex items-center justify-center gap-2"><Calendar className="w-3.5 h-3.5" strokeWidth={2} /> Book Your Strategy Session</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Pick a time that works for you.</h2>
            <p className="mt-4 text-[#4B5563] leading-relaxed max-w-2xl mx-auto">
              30 minutes with Alex. We'll walk through your Mogul Score™, review your personalized roadmap, and identify the highest-leverage next moves for {lead.business_name || "your business"}.
            </p>
          </div>
          <div
            className="calendly-inline-widget bg-white border border-gray-100"
            data-url={url}
            data-testid="calendly-widget"
            style={{ minWidth: "320px", height: "720px" }}
          />
          <p className="mt-4 text-xs text-[#6B7280] text-center">
            Can't see the calendar? <a href={`https://calendly.com/themoguladvisors-info/30min?${prefill}`} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Open scheduler in a new tab →</a>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ helpers ------------------------------ */
const inputCls = "w-full bg-transparent border-b border-gray-300 focus:border-gold focus:outline-none py-2 text-[#0A0A0A]";
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-[#6B7280] mb-2">{label}{required && <span className="text-gold"> *</span>}</span>
      {children}
    </label>
  );
}
